// src/components/forms/DepositActions.tsx — protect a deposit, and give it back.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Button, Field, Input, Modal, Money, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, toMinor } from './common';

type D = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);
const UK_SCHEMES = [['DPS', 'Deposit Protection Service'], ['TDS', 'Tenancy Deposit Scheme'], ['MYDEPOSITS', 'mydeposits']];

const useAfter = (onClose: () => void) => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['/deposits'] });
    void qc.invalidateQueries({ queryKey: ['tenancy-deposits'] });
    onClose();
  };
};

/**
 * England: protect within 30 days of receipt and serve the prescribed information,
 * or face a penalty of one to three times the deposit. The deadline is a stored
 * column, so this form shows exactly how long is left rather than a vague warning.
 */
export function ProtectDeposit({ open, onClose, deposit }: { open: boolean; onClose: () => void; deposit: D }) {
  const after = useAfter(onClose);
  const isUae = deposit.region === 'AE';
  const [form, setForm] = useState({
    scheme: isUae ? 'LANDLORD_HELD' : 'DPS', schemeReference: '', protectedOn: today(), prescribedInfoServedOn: today(),
  });
  const run = useMutation({
    mutationFn: async () => api.post(`/deposits/${deposit.id}/protect`, {
      scheme: form.scheme, schemeReference: form.schemeReference.trim(),
      protectedOn: form.protectedOn || undefined,
      prescribedInfoServedOn: form.prescribedInfoServedOn || null,
    }),
    onSuccess: after,
  });
  const days = deposit.daysUntilDeadline;
  return (
    <Modal open={open} onClose={onClose} title="Protect this deposit"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={!form.schemeReference.trim() || run.isPending}>{run.isPending ? 'Recording…' : 'Record protection'}</Button></>}>
      <div className="space-y-5">
        <p className="text-[13.5px] leading-relaxed text-ink-600">
          <Money minor={deposit.amountMinor} currency={deposit.amountCurrency} className="font-medium text-ink-900" /> held on {deposit.tenancyReference} · {deposit.propertyName} {deposit.unitReference}.
          {typeof days === 'number' && (days < 0
            ? <span className="mt-1 block font-medium text-red-600">The 30-day deadline passed {-days} days ago.</span>
            : <span className="mt-1 block text-ink-500">{days} days left of the 30-day deadline.</span>)}
        </p>
        <FormGrid>
          <Field label="Scheme">
            <Select value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })} className="w-full">
              {isUae ? <option value="LANDLORD_HELD">Held by the landlord</option> : UK_SCHEMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Scheme reference"><Input value={form.schemeReference} onChange={(e) => setForm({ ...form, schemeReference: e.target.value })} placeholder="DPS-2026-0001234" className="w-full" /></Field>
          <Field label="Protected on"><Input type="date" value={form.protectedOn} onChange={(e) => setForm({ ...form, protectedOn: e.target.value })} className="w-full" /></Field>
          <Field label="Prescribed information served" hint="Required alongside protection in England"><Input type="date" value={form.prescribedInfoServedOn} onChange={(e) => setForm({ ...form, prescribedInfoServedOn: e.target.value })} className="w-full" /></Field>
        </FormGrid>
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}

/** Returning the deposit, with anything withheld written down. */
export function ReleaseDeposit({ open, onClose, deposit }: { open: boolean; onClose: () => void; deposit: D }) {
  const after = useAfter(onClose);
  const [form, setForm] = useState({ deductions: '', notes: '' });
  const run = useMutation({
    mutationFn: async () => api.post(`/deposits/${deposit.id}/release`, {
      deductionsMinor: toMinor(form.deductions) ?? 0,
      deductionNotes: form.notes.trim() || undefined,
    }),
    onSuccess: after,
  });
  const deductions = toMinor(form.deductions) ?? 0;
  const returned = Number(deposit.amountMinor ?? 0) - deductions;
  const needsNotes = deductions > 0 && !form.notes.trim();
  return (
    <Modal open={open} onClose={onClose} title="Release this deposit"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={needsNotes || returned < 0 || run.isPending}>{run.isPending ? 'Releasing…' : 'Release deposit'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label={`Deductions (${deposit.amountCurrency ?? 'GBP'})`} hint="Leave blank to return it in full"><Input value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} inputMode="decimal" className="w-full" /></Field>
          <Field label="Returned to the tenant"><Input value={new Intl.NumberFormat('en-GB', { style: 'currency', currency: deposit.amountCurrency ?? 'GBP' }).format(Math.max(returned, 0) / 100)} disabled className="w-full" /></Field>
          <Wide><Field label="What is being withheld and why" hint={needsNotes ? 'Required whenever anything is deducted — the tenant can dispute it' : undefined}><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field></Wide>
        </FormGrid>
        {returned < 0 && <p className="text-[12.5px] text-red-600">Deductions are more than the deposit held.</p>}
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}
